import { meetingNotesDocToMarkdown, type MeetingNotesDoc } from '@kiao-voice/core/meeting-notes'
import type { JSONContent } from '@tiptap/core'
import { generateHTML, generateJSON } from '@tiptap/html'
import { meetingNotesExtensions } from './meeting-notes-extensions.js'

/**
 * The meeting note's schema, applied outside an editor.
 *
 * `meeting-notes-extensions.ts` is the one answer to「這份文件可以長什麼樣」;
 * everything that reads or writes a stored note comes through here so that
 * answer is given once. The schema — not a hand-written renderer and not a
 * sanitizer — is what decides which markup survives: a tag it does not declare
 * has nowhere to land, so it is dropped with its text kept, and injection is
 * impossible by construction rather than by blocklist.
 *
 * No React import, by design: apps/api renders notes through this module, and
 * the API has no business pulling a UI runtime. `@tiptap/html` resolves to a
 * happy-dom build under Node and to `window.DOMParser` in the browser, so the
 * same import works on the server, in the web app and in a test.
 */

export type { MeetingNotesDoc }

/** A note → the HTML that is stored in `content_html`. */
export function renderMeetingNotesHtml(doc: MeetingNotesDoc): string {
  return generateHTML(doc as unknown as JSONContent, meetingNotesExtensions)
}

/** Stored (or pasted, or hand-edited) HTML → the note it holds. */
export function parseMeetingNotesHtml(html: string): MeetingNotesDoc {
  return generateJSON(html, meetingNotesExtensions) as MeetingNotesDoc
}

/**
 * The single write path for user-edited content: whatever the browser handed
 * back, re-rendered from the document the schema could make of it.
 */
export function normalizeMeetingNotesHtml(html: string): string {
  return renderMeetingNotesHtml(parseMeetingNotesHtml(html))
}

/** Stored HTML → Markdown, for MCP, 複製 and 下載 `.md`. */
export function meetingNotesHtmlToMarkdown(html: string): string {
  return meetingNotesDocToMarkdown(parseMeetingNotesHtml(html))
}
